import React, {useContext, useEffect, useLayoutEffect, useState, useRef, startTransition} from "react";
import {Container, Button} from "react-bootstrap";
import {Link, useLocation, useHistory} from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import {useTranslation} from "react-i18next";

import AuthContext from "../contexts/AuthContext";
import TournamentHeader, { SCREENSHOT_TARGETS } from "../pages/tournaments/TournamentHeader";
import { ScreenshotButton } from "./ScreenshotButton";
import {tournamentCache, tournamentSubscribers} from "../utils/tournamentCache";
import {settingsSubscribers} from "../utils/tournamentSettingsBus";
import {ratingSubscribers} from "../utils/tournamentRatingBus";
import RatingTable from "../pages/ratings/RatingTable";
import {compactSearchSubscribers} from "../utils/compactSearchBus";
import MainHeader, { getModeCounterpart } from "./headers/MainHeader";
import BottomNav from "./BottomNav";
import TournamentSettingsModal from "../pages/tournaments/settings/TournamentSettingsModal";
import TournamentShareModal from "../pages/tournaments/export/TournamentShareModal";
import TournamentExportModal from "../pages/tournaments/export/TournamentExportModal";
import { CustomModal } from "./CustomModel/CustomModal";
import LoginForm from "./LoginForm";

import useLocalStorage from "../hooks/UseLocalStorage";
import i18n from "../i18n";
import { useTheme } from "../contexts/ThemeContext";
import TournamentMenu from "../pages/tournaments/TournamentMenu";

function DropdownItem({ onClick, href, img, text, active }) {
    // logout is an action, not real navigation - href='/logout' doesn't
    // match any route, so a Link's own history.push('/logout') would race
    // logoutUser's own redirect to /tournaments for control of the final
    // URL. Actions render as a plain link that only runs onClick.
    if (onClick && !href) {
        return (
            <li className={active ? 'active' : ''}>
                <a href="#" onClick={(e) => { e.preventDefault(); onClick(); }}><FeatherIcon icon={img} />{text}</a>
            </li>
        );
    }
    // Public View/Admin View pass both: href is a best-effort fallback
    // (middle-click/open-in-new-tab), onClick overrides normal left-click
    // navigation with a freshly-computed target - see
    // navigateToModeCounterpart's comment in Header for why href alone
    // can't be trusted.
    return (
        <li className={active ? 'active' : ''}>
            <Link to={href} onClick={(e) => { if (onClick) { e.preventDefault(); onClick(e); } }}><FeatherIcon icon={img} />{text}</Link>
        </li>
    );
}

// Preset widths for the Public View bottom-nav zoom control - fed straight
// into App.js's viewport-meta effect via ThemeContext's previewWidth. 0 is
// "off" (plain device-width, the normal mobile layout); 480 is a step up for
// larger phones/phablets, still under the tablet breakpoint band; 768
// deliberately sits in that tablet band (see useIsWideViewport.js); 1024
// clears it right at useIsWideViewport's own desktop breakpoint. Desktop
// (1920) was dropped - clicking into a match at that width broke inside the
// scaled preview iframe (TournamentPreviewFrame.js's CSS transform: scale()
// throws off fixed-position modals like MatchDetailsModal.js); 1024 is
// close enough to trigger the same desktop layout without the reported
// match-click bugs.
//
// The full list only ever makes sense as an *upgrade* from whatever the
// visitor is actually on - previewing a width smaller than the real device
// (e.g. a phone "previewing" itself at Mobile/Large Mobile, or a real
// desktop "previewing" a phone) isn't a preview of anything, just a worse
// version of the page already in front of them. See
// getAvailableResolutionPresets below for the actual per-device filtering.
const RESOLUTION_PRESETS = [
    { width: 0, label: "Mobile" },
    { width: 480, label: "Large Mobile" },
    { width: 768, label: "Tablet" },
    { width: 1024, label: "Laptop" },
];

// 0 (off) always stays available so an active preview can be turned back
// off regardless of device width; every other preset only qualifies if it's
// a genuine step up from the real device the visitor is actually on - a
// real desktop (deviceWidth >= 1024, the widest preset) is left with only
// the off entry, which the caller reads as "hide the control entirely".
const getAvailableResolutionPresets = (deviceWidth) =>
    RESOLUTION_PRESETS.filter(preset => preset.width === 0 || preset.width > deviceWidth);

export default function Header() {
    const {user, logoutUser} = useContext(AuthContext);
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation();

    const pathSegments = location.pathname.split("/").filter(Boolean);
    const tournamentIndex = pathSegments.indexOf("tournaments");
    const isTournamentPage = tournamentIndex !== -1 && pathSegments.length > tournamentIndex + 1;
    const tournamentId = isTournamentPage ? pathSegments[tournamentIndex + 1] : null;
    // the tournament detail page has no /admin route of its own anymore -
    // edit mode there is a ?admin=1 query param instead, toggled from the
    // page's own menu. Every other /admin/... page is still a real, separate
    // route, so keep the path check for those.
    const isAuthorized = isTournamentPage
        ? new URLSearchParams(location.search).get("admin") === "1"
        : location.pathname.startsWith("/admin");
    // Same ?tab= param Tournament.js itself reads (defaulting to
    // "information" the same way) - Header.js mounts as its own React tree
    // at the App.js level (see this file's own tournamentHeroBus/
    // tournamentSearchBus comments for why), so the bottom-nav Screenshot
    // button below has no other way to know which tab's content is
    // currently on screen.
    const screenshotTarget = SCREENSHOT_TARGETS[new URLSearchParams(location.search).get("tab") || "information"];
    // The merged "draw-stages" tab (Groups/Brackets) syncs its own active
    // stage/sub-tab to the URL too (?stage=<1-based position>, ?gridTab=
    // main|qualification|__all__ - see TournamentDrawStages.js/
    // TournamentGrids.js's own history.replace calls), same as ?tab= above -
    // reusable here for the same reason, so the downloaded file says which
    // stage it actually is instead of a generic "Draw" every time. Schedule
    // stages sync too (?stage=<uuid>), but that one's a real id, not a
    // human-readable position, so it's left alone rather than stuffing a
    // uuid into the filename.
    const screenshotStageSuffix = (() => {
        const params = new URLSearchParams(location.search);
        if (params.get("tab") !== "draw-stages") return "";
        const stagePos = params.get("stage");
        const gridTab = params.get("gridTab");
        const parts = [];
        if (stagePos) parts.push(`${t("Stage")}${stagePos}`);
        if (gridTab === "qualification") parts.push(t("Qualification"));
        return parts.length ? `_${parts.join("_")}` : "";
    })();

    const [tournament, setTournament] = useState(null);
    const [club, setClub] = useState(null);

    const languages = ["uk", "en"];
    const userBrowserLanguage = navigator.language || navigator.userLanguage;
    const [language, setLanguage] = useLocalStorage('language', languages.indexOf(userBrowserLanguage) > -1 ? userBrowserLanguage : "uk");

    const [open, setOpen] = useState(false);
    const [openLanguage, setOpenLanguage] = useState(false);
    const [openTheme, setOpenTheme] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsInitialTab, setSettingsInitialTab] = useState(null);
    const [ratingOpen, setRatingOpen] = useState(false);
    const [ratingHighlightPlayerId, setRatingHighlightPlayerId] = useState(null);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [compactSearch, setCompactSearch] = useState(null);
    const menuRef = useRef();
    const headerRef = useRef();

    useEffect(() => {
        const handler = (state) => setCompactSearch(state);
        compactSearchSubscribers.add(handler);
        return () => compactSearchSubscribers.delete(handler);
    }, []);

    // .main-header.tournament-mode is height:auto (TournamentHeader's own
    // title row + tabs row run taller than the generic 70px header, and its
    // real height also shifts with the tournament name wrapping to a second
    // line or its tab row overflowing) - .main's own margin-top is a flat
    // site-wide constant sized for the generic 70px header, so on tournament
    // pages the fixed header quietly overlapped a few px into .main's top
    // content, eating into whatever margin-top the first thing inside it
    // (Участники/Жеребкування/Розклад's own pull-bar) had. Measuring the
    // real height here and exposing it as a CSS var lets .main's tournament-
    // mode override (see .main-header.tournament-mode ~ .main) track it
    // instead of guessing another fixed number that would just as quickly
    // drift out of sync.
    useLayoutEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        const update = () => {
            document.documentElement.style.setProperty('--tournament-header-h', `${el.offsetHeight}px`);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const activeTab = new URLSearchParams(location.search).get('tab') || 'information';

    // "groups"/"brackets"/"draws" tournament tabs all map to the one merged
    // "draw-stages" settings tab (see TournamentSettingsModal.js) - it
    // replaced the separate Groups/Brackets settings screens. Shared by the
    // bottom nav's own Settings link and TournamentMenu's "..." dropdown.
    const openTournamentSettings = () => {
        const settingsTab = activeTab === "information" ? null
            : (activeTab === "groups" || activeTab === "brackets" || activeTab === "draws") ? "draw-stages"
            : activeTab;
        setSettingsInitialTab(settingsTab);
        setSettingsOpen(true);
    };

    const { theme, setTheme, accent, setAccent, compactTable, setCompactTable, previewWidth, setPreviewWidth } = useTheme();
    // Remembers the last "Screen size" preset per tournament (not globally -
    // ThemeContext.js's previewWidth is shared app-wide state, but a size
    // picked while looking at one tournament has no business following the
    // visitor into an unrelated one) so a returning visit lands straight
    // back on the previously-chosen width instead of always starting at
    // Mobile/off. One effect handles both directions off a single "did
    // tournamentId actually change" branch rather than two separate
    // restore/save effects: with two effects, restoring a stored value (a
    // setPreviewWidth call) itself changes previewWidth, which would
    // immediately re-fire a naive save effect and write that same value
    // straight back - harmless for a real switch, but on a switch to a
    // tournament with NO stored value the restore's own setPreviewWidth(0)
    // would get "saved" as an explicit 0 for a tournament that never had an
    // entry, and worse, a subsequent switch to a third tournament sharing
    // that same numeric width would never re-fire the save effect at all
    // (previewWidth wouldn't have changed), silently skipping its own real
    // save. Branching on tournamentId equality inside one effect sidesteps
    // all of that: a tournamentId change always restores (or resets) and
    // returns before ever reaching the save branch, and a previewWidth
    // change with the SAME tournamentId always saves.
    const previewWidthTournamentRef = useRef(undefined);
    useEffect(() => {
        if (previewWidthTournamentRef.current !== tournamentId) {
            previewWidthTournamentRef.current = tournamentId;
            let stored = 0;
            if (tournamentId) {
                try {
                    const raw = parseInt(localStorage.getItem(`app-preview-width:${tournamentId}`), 10);
                    if (!Number.isNaN(raw)) stored = raw;
                } catch {}
            }
            if (stored !== previewWidth) setPreviewWidth(stored);
            return;
        }
        if (!tournamentId) return;
        try {
            if (previewWidth > 0) localStorage.setItem(`app-preview-width:${tournamentId}`, String(previewWidth));
            else localStorage.removeItem(`app-preview-width:${tournamentId}`);
        } catch {}
    }, [tournamentId, previewWidth, setPreviewWidth]);
    // Reactive, not read once - resizing a desktop browser window (or
    // rotating a tablet) across a preset's width should immediately drop it
    // from/add it back to the cycle, same as useIsWideViewport.js's own
    // resize handling elsewhere in this file.
    const [deviceWidth, setDeviceWidth] = useState(() => window.innerWidth);
    useEffect(() => {
        const onResize = () => setDeviceWidth(window.innerWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    const availableResolutionPresets = getAvailableResolutionPresets(deviceWidth);
    const resolutionIndex = Math.max(0, availableResolutionPresets.findIndex(preset => preset.width === previewWidth));
    const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');
    const modeCounterpart = user ? getModeCounterpart(location.pathname) : null;
    // Best-effort href (e.g. for middle-click/open-in-new-tab) - kept in sync
    // with the router's own location.search where possible, but see
    // goToPublicMode/goToAdminMode below for why this alone isn't reliable.
    const modeCounterpartHref = modeCounterpart ? `${modeCounterpart}${location.search}` : modeCounterpart;
    const publicModeHref = isAuthorized ? modeCounterpartHref : location.pathname + location.search;
    const adminModeHref = isAuthorized ? location.pathname + location.search : modeCounterpartHref;
    // Filters (e.g. tournaments list's ?sport=... chips) live in the URL, but
    // TournamentsList.js writes them there via a raw window.history.replaceState
    // call instead of react-router's own history object (to avoid a real route
    // re-render on every chip click) - so react-router's `location.search` above
    // silently falls behind the real address bar the moment a filter changes,
    // and a href built from it goes stale. Reading window.location.search fresh
    // at click time (instead of trusting the href) is the only way to actually
    // carry the current filters across the mode switch.
    // Two direction-specific handlers (not one shared toggle) - clicking the
    // already-active item must stay a no-op rather than bouncing to the
    // counterpart, same as the original href-based logic above.
    const goToPublicMode = (e) => {
        if (!isAuthorized || !modeCounterpart) return;
        e.preventDefault();
        history.push(`${modeCounterpart}${window.location.search}`);
    };
    const goToAdminMode = (e) => {
        if (isAuthorized || !modeCounterpart) return;
        e.preventDefault();
        history.push(`${modeCounterpart}${window.location.search}`);
    };
    // The nav container itself mounts for every tournament page visitor,
    // editor or not - Share/Export don't depend on can_edit, so they're
    // always there. Only the editor-only items (mode-switch, Settings)
    // inside it gate on showTournamentBottomNav below. Keeping the
    // container itself unconditional avoids the old jank this used to have
    // when it depended on the not-yet-loaded can_edit check: showing the
    // editor nav optimistically then tearing it down (or, before that
    // existed, showing nothing at all) the moment a non-editor's data
    // arrived read as the header/bottom nav visibly jumping on every load.
    //
    // Strict `tournament?.can_edit === true` (no fallback while tournament
    // is still null) regressed the opposite, far more common case: an admin
    // opening their OWN tournament in admin mode (?admin=1) now saw the
    // editor nav (mode-switch/Settings) only pop in once the fetch resolved
    // - reading as "loads in user mode, then jumps to admin" on every single
    // admin navigation. `isAuthorized` (the ?admin=1 param, already resolved
    // synchronously) is the right optimistic guess for that gap, same
    // trade-off Tournament.js's own canEdit/canWrite already make for the
    // tab content itself - right for the overwhelmingly common case, and
    // self-corrects the moment real data arrives on the rare case it
    // guessed wrong (a shared admin link to a tournament this user doesn't
    // actually own).
    const showTournamentBottomNav = isTournamentPage && user && (tournament ? tournament?.can_edit === true : isAuthorized);

    useEffect(() => {
        if (!tournamentId) {
            setClub(null);
            setTournament(null);
            return;
        }

        const cached = tournamentCache[tournamentId];
        // Same guard Tournament.js's own cachedMatchesMode uses: a tournament
        // cached from a public-view visit has can_edit:false baked in.
        // Seeding it here unconditionally (as this used to) while mounting
        // into admin mode (?admin=1) briefly showed the public header/bottom
        // nav - Tournament.js's own properly-guarded fetch resolves a beat
        // later and republishes the real admin copy through tournamentSubscribers
        // below, which is what actually updated this component - reading as
        // the header flashing user-mode then jumping to admin. Only seed from
        // cache when it actually matches the mode we're mounting into; the
        // subsequent fetch fills tournament in either way.
        if (cached && (!isAuthorized || cached.can_edit)) {
            setTournament(cached);
            if (cached.club_name) {
                setClub({ image: cached.club_logo, name: cached.club_name });
            }
        } else {
            setTournament(null);
            setClub(null);
        }
        const handleUpdate = (id, data) => {
            if (id === tournamentId) {
                setTournament(data);
                if (data?.club_name) {
                    setClub({ name: data.club_name, image: data.club_logo });
                } else {
                    setClub(null);
                }
            }
        };
        tournamentSubscribers.add(handleUpdate);
        return () => tournamentSubscribers.delete(handleUpdate);
    }, [tournamentId, isAuthorized]);

    useEffect(() => {
        const handleOpenSettings = (tab) => {
            setSettingsInitialTab(tab || null);
            setSettingsOpen(true);
        };
        settingsSubscribers.add(handleOpenSettings);
        return () => settingsSubscribers.delete(handleOpenSettings);
    }, []);

    // Rating badges (Участники list/grid, Результати tab's own cards) open
    // this modal through the same always-mounted-Header bus as tournament
    // settings, rather than by pushing a `tab=` query param - Tournament.js
    // switches tab content with a plain `switch (activeTab)`, so a `tab=`
    // change unmounts whatever tab the badge was clicked from and mounts a
    // fresh one, refetching everything and resetting scroll/search state -
    // effectively a reload for no reason, since the modal doesn't actually
    // need the underlying tab to change at all.
    useEffect(() => {
        const handleOpenRating = (playerId) => {
            setRatingHighlightPlayerId(playerId || null);
            setRatingOpen(true);
        };
        ratingSubscribers.add(handleOpenRating);
        return () => ratingSubscribers.delete(handleOpenRating);
    }, []);

    // Shared rating links (RatingTable's own "copy link" button) still land
    // via a real URL - `show_rating=true` here opens the modal for that
    // fresh-navigation case, distinct from the bus above which handles
    // same-session clicks without touching the URL at all.
    useEffect(() => {
        if (!isTournamentPage) return;
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.get("show_rating") === "true") {
            setRatingHighlightPlayerId(queryParams.get("highlight_player") || null);
            setRatingOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTournamentPage, tournamentId]);

    useEffect(() => {
        if (language) {
            if (i18n.isInitialized) {
                i18n.changeLanguage(language);
            } else if (i18n.on) {
                i18n.on('initialized', () => {
                    i18n.changeLanguage(language);
                });
            }
        }
    }, [language]);

    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
                setOpenLanguage(false);
                setOpenTheme(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleLanguageChange = (lang) => () => {
        i18n.changeLanguage(`${lang}`);
        setLanguage(`${lang}`);
    };

    function DropdownLanguage({ language: lang, name }) {
        return (
            <li onClick={handleLanguageChange(lang)} className={lang === language ? 'active' : ''}>
                {lang === 'uk'
                    ? <img src={require(`../fonts/flags/1x1/ua.svg`).default} alt="UA"/>
                    : <img src={require(`../fonts/flags/1x1/us.svg`).default} alt="US"/>}
                <span>{name}</span>
            </li>
        );
    }

    const rightControls = (
        <div className="menu-container" ref={menuRef}>
            <ul className="absolute_menu mode">
                {/* Permanent search/create icons on these pages (see
                    useCompactToolbar) - not a scroll-triggered stand-in for
                    theme/language anymore, just two extra always-there
                    icons alongside them. Tapping search jumps straight to
                    the page's own real search box (scrolls it into view and
                    expands/focuses it) instead of re-implementing search
                    here; tapping it again while already open closes it, same
                    toggle as the tournament page's own search icon. Tapping
                    "+" fires the page's create action directly. */}
                {!isTournamentPage && compactSearch && (
                    <li className="fade-in-icon" key="compact-search-icon">
                        <div
                            className="language"
                            // Without this, clicking the icon while the page's
                            // real search input is focused steals focus first
                            // (native mousedown behavior) - that fires the
                            // input's own onBlur (collapseToolbar, see e.g.
                            // TournamentsList.js's TournamentSearch), which
                            // publishes isOpen:false *before* this click
                            // handler runs, so it reads isOpen as already
                            // false and calls onExpand() right back instead
                            // of the onCollapse() a second tap should do -
                            // same race already fixed for the tournament
                            // page's own search icon (TournamentPlayers.js/
                            // TournamentDrawStages.js).
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => (compactSearch.isOpen ? compactSearch.onCollapse() : compactSearch.onExpand())}
                            title={t("Search")}
                        >
                            <FeatherIcon icon="search" />
                        </div>
                    </li>
                )}

                {!isTournamentPage && compactSearch?.onCreate && (
                    <li className="fade-in-icon" key="compact-create">
                        <a href="#" className="action-button btn-green compact-create" onClick={(e) => { e.preventDefault(); compactSearch.onCreate(e); }} title={t("Create")}>
                            <FeatherIcon icon="plus" size={24} />
                        </a>
                    </li>
                )}

                {!isTournamentPage && (
                    <li className="mode-selection fade-in-icon" key="theme-selection">
                        <div className="language" onClick={() => { setOpen(false); setOpenLanguage(false); setOpenTheme(!openTheme); }}>
                            <FeatherIcon icon={theme === 'light' ? 'sun' : 'moon'} />
                        </div>
                        <div className={`dropdown-menu ${openTheme ? 'block_active' : 'block_inactive'}`}>
                            <h3>{t('Theme selection')}</h3>
                            <div className="language_menu">
                                <ul>
                                    <li
                                        onClick={() => setTheme('light')}
                                        className={theme === 'light' ? 'active' : ''}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <FeatherIcon icon="sun" />
                                        <span>{t('Light Mode')}</span>
                                    </li>

                                    <li
                                        onClick={() => setTheme('dark')}
                                        className={theme === 'dark' ? 'active' : ''}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <FeatherIcon icon="moon" />
                                        <span>{t('Dark Mode')}</span>
                                    </li>
                                    <li className="accent-selectors" style={{ display: 'flex', gap: '10px', padding: '10px 20px' }}>
                                        <div
                                            title="Grass"
                                            onClick={() => setAccent('grass')}
                                            style={{ width: 20, height: 20, borderRadius: '50%', background: '#539447', cursor: 'pointer', border: accent === 'grass' ? '2px solid var(--darker-green)' : '2px solid var(--body-background)'}}
                                        />
                                        <div
                                            title="Clay"
                                            onClick={() => setAccent('clay')}
                                            style={{ width: 20, height: 20, borderRadius: '50%', background: '#c6806a', cursor: 'pointer', border: accent === 'clay' ? '2px solid var(--darker-green)' : '2px solid var(--body-background)'}}
                                        />
                                        <div
                                            title="Hard"
                                            onClick={() => setAccent('hard')}
                                            style={{ width: 20, height: 20, borderRadius: '50%', background: '#5290cd', cursor: 'pointer', border: accent === 'hard' ? '2px solid var(--darker-green)' : '2px solid var(--body-background)'}}
                                        />
                                    </li>
                                    {/* Independent of Light/Dark above - a layout density toggle, not a
                                        third theme variant, so it's its own row/switch rather than
                                        joining the sun/moon list items. See
                                        frontend-tasks/2026-09-02-compact-table-mode.md.
                                        User-facing-only: only affects public list/tournament views,
                                        so an admin browsing their own /admin/* pages (isAuthorized
                                        here, same admin/public split as the tournament page's own
                                        Admin/Public toggle below) has no use for it - hidden rather
                                        than shown-but-inert. */}
                                    {/* Compact Table temporarily disabled - see ThemeContext.js.
                                        {!isAuthorized && (
                                        <li
                                            className="compact-table-toggle"
                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', cursor: 'pointer' }}
                                            onClick={() => setCompactTable(!compactTable)}
                                        >
                                            <span>{t('Compact table')}</span>
                                            <label className="switch" onClick={(e) => e.stopPropagation()}>
                                                <input type="checkbox" checked={compactTable} onChange={() => setCompactTable(!compactTable)} />
                                                <span />
                                            </label>
                                        </li>
                                    )} */}
                                </ul>
                            </div>
                        </div>
                    </li>
                )}

                {!isTournamentPage && (
                    <li className="fade-in-icon language-selection" key="language-selection">
                        <div className="language" onClick={() => { setOpen(false); setOpenTheme(false); setOpenLanguage(!openLanguage); }}>
                            {language === 'uk'
                                ? (<img src={require(`../fonts/flags/1x1/ua.svg`).default} alt="UA"/>)
                                : (<img src={require(`../fonts/flags/1x1/us.svg`).default} alt="US"/>)}
                        </div>
                        <div className={`dropdown-menu ${openLanguage ? 'block_active' : 'block_inactive'}`}>
                            <h3>{t('Language selection')}</h3>
                            <div className="language_menu">
                                <ul>
                                    <DropdownLanguage name="Українська" language="uk" />
                                    <DropdownLanguage name="English" language="en" />
                                </ul>
                            </div>
                        </div>
                    </li>
                )}

                <li className="second">
                    {isTournamentPage ? (
                        <TournamentMenu
                            id={tournamentId}
                            user={user}
                            tournament={tournament}
                            isAuthorized={isAuthorized}
                            setOpenLanguage={setOpenLanguage}
                            setOpenTheme={setOpenTheme}
                            theme={theme}
                            setTheme={setTheme}
                            accent={accent}
                            setAccent={setAccent}
                            compactTable={compactTable}
                            setCompactTable={setCompactTable}
                            language={language}
                            handleLanguageChange={handleLanguageChange}
                            onOpenSettings={openTournamentSettings}
                        />
                    ) : (
                        user ? (
                            <div className="menu-trigger" onClick={() => { setOpenLanguage(false); setOpenTheme(false); setOpen(!open); }}>
                                <FeatherIcon icon="user"/>
                                <div className={`dropdown-menu ${open ? 'block_active' : 'block_inactive'}`}>
                                    <h3>{user.email}</h3>
                                    <ul>
                                        {modeCounterpart && (
                                            <>
                                                <li className="dropdown-menu-label">{t('Select view mode')}</li>
                                                <DropdownItem
                                                    img={'eye'}
                                                    text={t('Public View')}
                                                    href={publicModeHref}
                                                    active={!isAuthorized}
                                                    onClick={goToPublicMode}
                                                />
                                                <DropdownItem
                                                    img={'edit'}
                                                    text={t('Admin View')}
                                                    href={adminModeHref}
                                                    active={isAuthorized}
                                                    onClick={goToAdminMode}
                                                />
                                                <li className="dropdown-menu-divider"></li>
                                            </>
                                        )}
                                        {user.is_superuser ? (
                                            <DropdownItem img={'edit'} text={t('Intennis')} href={'/intennis'}/>
                                        ) : (
                                            <>
                                                <DropdownItem img={'edit'} text={t('Profile')} href={'/profile'}/>
                                                <DropdownItem img={'key'} text={t('Balance')} href={'/balance'}/>
                                            </>
                                        )}
                                        <DropdownItem img={'log-out'} text={t('Logout')} href={'/logout'} onClick={logoutUser}/>
                                    </ul>
                                </div>
                            </div>
                        ) : (
                            <div className="login">
                                <Button variant="primary" onClick={() => setIsLoginOpen(true)}>{t("Short login")}</Button>
                            </div>
                        )
                    )}
                </li>
            </ul>
        </div>
    );

    return (
        <>
        {/* only the actual top bar lives inside .main-header (position: fixed) -
            BottomNav/tournament-bottom-nav/modals used to be nested in here too,
            which meant any transform (or will-change: transform) put on
            .main-header - e.g. for the scroll-hide animation - turned it into
            the containing block for their own position:fixed, pinning
            "bottom: 0" to the 70px-tall header instead of the viewport. */}
        <header ref={headerRef} className={`main-header ${isTournamentPage ? 'tournament-mode' : ''} ${isTournamentPage ? 'tournament-nav-visible' : ''}`}>
            <Container>
                {isTournamentPage ?
                    <TournamentHeader
                        user={user}
                        club={club}
                        tournament={tournament}
                        tournamentId={tournamentId}
                        isAuthorized={isAuthorized}
                        rightControls={rightControls}
                    />
                    : <MainHeader
                        user={user}
                        logoutUser={logoutUser}
                        rightControls={rightControls}
                    />
                }
            </Container>
        </header>
            {!isTournamentPage && <BottomNav />}
            {isTournamentPage && (
                <TournamentSettingsModal
                    tournamentId={tournamentId}
                    tournament={tournament}
                    setTournament={setTournament}
                    isOpen={settingsOpen}
                    initialTab={settingsInitialTab}
                    onClose={() => setSettingsOpen(false)}
                />
            )}
            {isTournamentPage && tournament?.rating && (
                <CustomModal
                    isOpen={ratingOpen}
                    onClose={() => { setRatingOpen(false); setRatingHighlightPlayerId(null); }}
                    maxWidth="1200px"
                    hideCloseButton
                >
                    <RatingTable
                        ratingId={tournament.rating}
                        highlightPlayerId={ratingHighlightPlayerId}
                        isOpen={ratingOpen}
                        onClose={() => { setRatingOpen(false); setRatingHighlightPlayerId(null); }}
                    />
                </CustomModal>
            )}
            {isTournamentPage && (
                <nav className="tournament-bottom-nav">
                    <ul>
                        {showTournamentBottomNav && (
                            <>
                                <li className="mode-switch">
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const params = new URLSearchParams(location.search);
                                            if (isAuthorized) params.delete("admin"); else params.set("admin", "1");
                                            // this can reveal already-mounted content newly
                                            // rendering an is_authorized branch that hasn't
                                            // been shown yet (e.g. TournamentInformationForm's
                                            // editable fields) - startTransition keeps that
                                            // from being treated as urgent synchronous input,
                                            // which React 18 otherwise warns about/blanks the
                                            // UI to a loading fallback for.
                                            startTransition(() => {
                                                history.replace({ pathname: `/tournaments/${tournamentId}`, search: params.toString() });
                                            });
                                        }}
                                    >
                                        <FeatherIcon icon={isAuthorized ? "edit" : "eye"} size={20} />
                                        <span>{t(isAuthorized ? "Admin" : "Public View")}</span>
                                    </a>
                                </li>
                                {isAuthorized && (
                                    <li className="nav-divider">
                                        <a
                                            href="#"
                                            onClick={(e) => { e.preventDefault(); openTournamentSettings(); }}
                                        >
                                            <FeatherIcon icon="settings" size={20} />
                                            <span>{t("Settings")}</span>
                                        </a>
                                    </li>
                                )}
                            </>
                        )}
                        {/* Sits right after the mode-switch toggle (same nav-divider bar
                            Settings gets above, for the same "set off from the rest of the
                            nav" reason) rather than down by Share/Export - "Request desktop
                            site" at a chosen width, not a plain CSS layout switch (see
                            ThemeContext.js's previewWidth/App.js's viewport effect for how
                            the actual wide-viewport trick works). An admin editing this
                            tournament needs its own narrow-width inputs at their real size,
                            not one of these emulated viewports (see App.js's own viewport
                            effect, which enforces that priority regardless of this picker's
                            last-set state) - Public View only.
                            No picker/modal - same plain nav button as Share/Export, just
                            stepping through availableResolutionPresets, now with its own -/+
                            (no loupe icon - a single icon read as "tap to change size" but
                            gave no hint which direction a tap would go, and every tap only
                            ever moved forward, wrapping back to Mobile/off past the widest
                            available preset). +/- move through the same list one step at a
                            time in either direction instead, clamped at both ends (not
                            wrapping) - Math.max/min below, not modulo - so +/- stop dead at
                            Mobile/off and the widest preset instead of looping back around
                            past them.
                            Hidden once nothing on getAvailableResolutionPresets's list is
                            wider than the real device (a real desktop already at/above the
                            widest preset, 1024) - only the always-present "off" entry would
                            be left to step to, i.e. previewing nothing. */}
                        {!isAuthorized && availableResolutionPresets.length > 1 && (
                            <li className={`screen-size-nav nav-divider ${previewWidth ? "active" : ""}`}>
                                <div className="screen-size-wrap">
                                    <span className="screen-size-steps">
                                        <button
                                            type="button"
                                            className="screen-size-step"
                                            aria-label={t("Decrease")}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setPreviewWidth(availableResolutionPresets[Math.max(resolutionIndex - 1, 0)].width);
                                            }}
                                        >
                                            <FeatherIcon icon="minus" size={14} />
                                        </button>
                                        {/* The actual width in px, not just the preset label below (which
                                            never changes) - +/- moved the iframe's real rendered width all
                                            along, but with nothing on this button itself showing it, telling
                                            480 from 768 from 1024 meant either eyeballing the preview's own
                                            proportions or opening Settings. "off" shows a dash rather than 0 -
                                            0 reads as a width, not as "no preview". */}
                                        <span className="screen-size-value">{previewWidth || "–"}</span>
                                        <button
                                            type="button"
                                            className="screen-size-step"
                                            aria-label={t("Increase")}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setPreviewWidth(availableResolutionPresets[Math.min(resolutionIndex + 1, availableResolutionPresets.length - 1)].width);
                                            }}
                                        >
                                            <FeatherIcon icon="plus" size={14} />
                                        </button>
                                    </span>
                                    <span>{t("Screen size")}</span>
                                </div>
                            </li>
                        )}
                        {/* User-facing-only, same as the theme dropdown's own copy of this
                            toggle above - an admin editing this tournament (isAuthorized)
                            has no use for it, only Public View does.
                            Compact Table temporarily disabled - see ThemeContext.js.
                        {!isAuthorized && (
                            <li className={compactTable ? "active" : ""}>
                                <button type="button" onClick={() => setCompactTable(!compactTable)}>
                                    <FeatherIcon icon={compactTable ? "minimize-2" : "maximize-2"} size={20} />
                                    <span>{compactTable ? t("Compact") : t("Standard mode")}</span>
                                </button>
                            </li>
                        )} */}
                        <li>
                            <a href="#" onClick={(e) => { e.preventDefault(); setIsShareOpen(true); }}>
                                <FeatherIcon icon="share-2" size={20} />
                                <span>{t("Share")}</span>
                            </a>
                        </li>
                        <li>
                            <a href="#" onClick={(e) => { e.preventDefault(); setIsExportOpen(true); }}>
                                <FeatherIcon icon="download" size={20} />
                                <span>{t("Export")}</span>
                            </a>
                        </li>
                        {/* Shown in both Public View and admin mode - organizers already
                            have the full Export modal above (Table/Print mode, per
                            block/stage, with PDF/PNG/Excel options), but this is the quick
                            one-tap version of just whatever tab (and, for Draw, stage/
                            sub-tab - see screenshotStageSuffix above) is on screen right
                            now. Always renders the public/user-facing view even when
                            triggered from admin mode - see ScreenshotButton's own
                            admin-param stripping. */}
                        {screenshotTarget && (
                            <ScreenshotButton
                                key={`${screenshotTarget.targetId}-${screenshotStageSuffix}`}
                                targetId={screenshotTarget.targetId}
                                fileName={`${screenshotTarget.fileName}${screenshotStageSuffix}`}
                                previewWidth={previewWidth}
                            />
                        )}
                    </ul>
                </nav>
            )}
            {isTournamentPage && (
                <TournamentShareModal
                    isOpen={isShareOpen}
                    onClose={() => setIsShareOpen(false)}
                    tournamentId={tournamentId}
                    tournament={tournament}
                />
            )}
            {isTournamentPage && (
                <TournamentExportModal
                    isOpen={isExportOpen}
                    onClose={() => setIsExportOpen(false)}
                    tournamentId={tournamentId}
                    tournament={tournament}
                />
            )}
            {!user && (
                <CustomModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} maxWidth="450px" compact>
                    <div className="auth-card">
                        <LoginForm onClose={() => setIsLoginOpen(false)} />
                    </div>
                </CustomModal>
            )}
        </>
    );
}